import {makeAutoObservable, observable} from 'mobx'
import {logger} from 'yougile-util'
import {DataObjectId} from 'yougile-model'
import {createUseStore} from '@containers/hooks'
import {
  AppPersistence,
  AppState,
  BoardViewType
} from '@extensions/app-persistence'
import {PageService} from '@extensions/page-service'

interface State {
  view: BoardViewType
  zoom: number
}

export class BoardViewStore {
  state: State = {
    view: 'board',
    zoom: 1
  }

  constructor() {
    makeAutoObservable(
      this,
      {
        state: observable.shallow
      },
      {autoBind: true}
    )
  }

  init(appState: AppState | undefined): void {
    if (!appState) {
      return
    }
    this.state.view = this.getDefaultView()
    this.state.zoom = appState.timeline?.zoom ?? 1
  }

  updateDefaultView(): void {
    this.state.view = this.getDefaultView()
  }

  setView(view: BoardViewType): void {
    this.state.view = view
    this.saveDefaultScale(view)
  }

  setZoom(zoom: number): void {
    this.state.zoom = zoom
    AppPersistence.save()
  }

  private getDefaultView(): BoardViewType {
    const boardId = PageService.getCurrentBoard()?.id
    const data = localStorage.getItem('board-view')

    if (data && boardId) {
      try {
        const map = JSON.parse(data) as {[boardId: DataObjectId]: BoardViewType}
        return map[boardId] ?? 'board'
      } catch (e) {
        logger.warn('Cannot parse board-view from localStorage', e)
      }
    }

    return 'board'
  }

  private saveDefaultScale(value: BoardViewType): void {
    const boardId = PageService.getCurrentBoard()?.id
    const data = localStorage.getItem('board-view')
    if (!boardId) {
      return
    }

    try {
      const map: {[boardId: DataObjectId]: BoardViewType} = data
        ? JSON.parse(data)
        : {}
      map[boardId] = value
      localStorage.setItem('board-view', JSON.stringify(map))
    } catch (e) {
      logger.warn('Cannot parse board-view from localStorage', e)
    }
  }
}

export const [BoardViewContext, useBoardViewStore] =
  createUseStore(BoardViewStore)

export const boardViewStore = new BoardViewStore()
